// 2D wizard drawing (hat, computer head, robe) for the character corner and
// the locker preview. Colours come from the equipped items; every hat and
// robe shape gets a simple sketch of its 3D model (three/cosmetics.ts).
import type { ReactElement } from "react";
import { ITEMS_BY_ID, type WizardLook } from "@wp/shared";

interface Look {
  color: string;
  accent: string;
  shape: string;
}

function look(id: string, fallback: { color: string; accent: string; shape?: string }): Look {
  const l = ITEMS_BY_ID.get(id)?.look;
  return { color: l?.color ?? fallback.color, accent: l?.accent ?? fallback.accent, shape: l?.shape ?? fallback.shape ?? "" };
}

const INK = "#111";
const ROBE = "M30 98 L38 62 Q50 56 62 62 L70 98 Z";
const GOLD = "#e8b923";

/** A five-point star path centred on (x, y). */
function star(x: number, y: number, r: number): string {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const k = i % 2 === 0 ? r : r * 0.42;
    pts.push(`${(x + Math.cos(a) * k).toFixed(2)} ${(y + Math.sin(a) * k).toFixed(2)}`);
  }
  return `M${pts.join(" L")} Z`;
}

/** A four-point sparkle centred on (x, y). */
function sparkle(x: number, y: number, r: number): string {
  const t = r * 0.25;
  return `M${x} ${y - r} L${x + t} ${y - t} L${x + r} ${y} L${x + t} ${y + t} L${x} ${y + r} L${x - t} ${y + t} L${x - r} ${y} L${x - t} ${y - t} Z`;
}

/** An almond eye with an iris (angel robe). */
function Eye({ x, y, gold }: { x: number; y: number; gold: string }) {
  return (
    <g>
      <path d={`M${x - 4} ${y} Q${x} ${y - 3.2} ${x + 4} ${y} Q${x} ${y + 3.2} ${x - 4} ${y} Z`} fill="#fff" stroke={gold} strokeWidth="0.8" />
      <circle cx={x} cy={y} r="1.5" fill="#3f7fd0" />
      <circle cx={x} cy={y} r="0.6" fill={INK} />
    </g>
  );
}

/** Tea Time: fewer, much bigger stars. */
const BIG_STARS: [number, number, number][] = [
  [41, 74, 4.2],
  [58, 82, 4.6],
  [44, 91, 3.6],
];

const STARS: [number, number, number][] = [
  [40, 76, 2.2],
  [57, 70, 1.8],
  [47, 88, 2],
  [61, 90, 2.2],
  [36, 92, 1.6],
  [53, 80, 1.5],
  [43, 68, 1.4],
];

const SPARKLES: [number, number, number][] = [
  [42, 70, 2.6],
  [57, 76, 2],
  [47, 88, 2.4],
  [62, 92, 1.6],
  [38, 94, 1.4],
];

function Robe({ robe }: { robe: Look }): ReactElement {
  const body = <path d={ROBE} fill={robe.color} stroke={INK} strokeWidth="1.5" />;
  const stripe = <path d="M44 62 L50 98 L56 62" fill="none" stroke={robe.accent} strokeWidth="2" />;
  const stars = STARS.map(([x, y, r]) => <path key={`${x}:${y}`} d={star(x, y, r)} fill={robe.accent} />);
  switch (robe.shape) {
    case "bigStars":
      // Tea Time: a few big stars.
      return (
        <g>
          {body}
          {BIG_STARS.map(([x, y, r]) => (
            <path key={`${x}:${y}`} d={star(x, y, r)} fill={robe.accent} />
          ))}
          {stripe}
        </g>
      );
    case "digitalSuper":
      return (
        <g>
          {/* grey shirt, black pleated skirt with teal trim, long teal tie */}
          <path d={ROBE} fill={robe.color} stroke={INK} strokeWidth="1.5" />
          <path d="M34 80 L66 80 L70 98 L30 98 Z" fill="#1a1a1e" stroke={INK} strokeWidth="1" />
          <path d="M30.6 96 L69.4 96" stroke={robe.accent} strokeWidth="1.6" />
          <path d="M40 80 L38 97 M46 80 L45 97 M54 80 L55 97 M60 80 L62 97" stroke="#3a3a40" strokeWidth="0.7" />
          <path d="M44 60 L50 65 L56 60 L54 58 L50 62 L46 58 Z" fill={robe.color} stroke={INK} strokeWidth="0.8" />
          <path d="M48.5 64 L51.5 64 L52.5 84 L50 87 L47.5 84 Z" fill={robe.accent} stroke={INK} strokeWidth="0.6" />
        </g>
      );
    case "stars":
      return (
        <g>
          {body}
          {stars}
          {stripe}
        </g>
      );
    case "plain":
      return body;
    case "maid":
      return (
        <g>
          {body}
          {/* frilly petticoat, apron with a ruffled hem and a bib, white collar */}
          <path d="M31 97 q2.5 3 5 0 q2.5 3 5 0 q2.5 3 5 0 q2.5 3 5 0 q2.5 3 5 0 q2.5 3 5 0 q2.5 3 5 0 q2.5 3 3 0" fill={robe.accent} stroke={INK} strokeWidth="0.6" />
          <path d="M41 71 L59 71 L63 93 L37 93 Z" fill={robe.accent} stroke={INK} strokeWidth="0.8" />
          <path d="M37 93 q2.2 3 4.4 0 q2.2 3 4.4 0 q2.2 3 4.4 0 q2.2 3 4.4 0 q2.2 3 4.4 0 q2.2 3 4.4 0" fill={robe.accent} stroke={INK} strokeWidth="0.6" />
          <path d="M45 63 L55 63 L56 71 L44 71 Z" fill={robe.accent} stroke={INK} strokeWidth="0.8" />
          <path d="M41 61 q2 3 4 0 q2 3 4 0 q2 3 4 0 q2 3 4 0 q2 3 2 0" fill={robe.accent} stroke={INK} strokeWidth="0.6" />
        </g>
      );
    case "fur":
      return (
        <g>
          {body}
          <path d="M50 62 L50 95" stroke={robe.accent} strokeWidth="5" strokeLinecap="round" />
          <path d="M32 95.5 L68 95.5" stroke={robe.accent} strokeWidth="5" strokeLinecap="round" />
          <ellipse cx="50" cy="61" rx="10" ry="3.2" fill={robe.accent} />
        </g>
      );
    case "invisible":
      // Glossy black cloth with a few sparkles.
      return (
        <g>
          <path d={ROBE} fill="#07070a" stroke={INK} strokeWidth="1.5" />
          {SPARKLES.map(([x, y, r]) => (
            <path key={`${x}:${y}`} d={sparkle(x, y, r)} fill={robe.accent} />
          ))}
        </g>
      );
    case "pigeon":
      return (
        <g>
          {body}
          {/* feathers, folded wings at the sides, pink feet */}
          <path d="M39 76 q3 3 6 0 q3 3 6 0 q3 3 6 0 M37 86 q3 3 6 0 q3 3 6 0 q3 3 6 0 q3 3 6 0" fill="none" stroke="#c9ccd2" strokeWidth="0.8" />
          <path d="M37 66 Q28 80 33 95 Q39 82 40 67 Z" fill="#d6d9de" stroke={INK} strokeWidth="0.8" />
          <path d="M63 66 Q72 80 67 95 Q61 82 60 67 Z" fill="#d6d9de" stroke={INK} strokeWidth="0.8" />
          <path d="M44 97 l-3 2.5 M44 97 l0 3 M44 97 l3 2.5 M56 97 l-3 2.5 M56 97 l0 3 M56 97 l3 2.5" stroke={robe.accent} strokeWidth="1.4" strokeLinecap="round" />
        </g>
      );
    case "hotCoat":
      // Grey stone with dark cracks and lava running down both sides to the hem.
      return (
        <g>
          {body}
          <path d="M44 70 L47 76 L45 82 M55 68 L53 75 L56 81 L54 88 M47 76 L51 79 M45 88 L49 93" fill="none" stroke="#3a3836" strokeWidth="0.8" strokeLinecap="round" />
          <g fill="none" strokeLinecap="round" stroke={robe.accent}>
            <path d="M39.5 64 Q37.5 72 38 79 Q36 88 32.5 97 M42 70 Q40.5 82 37.5 97 M60.5 64 Q62.5 72 62 79 Q64 88 67.5 97 M58 72 Q59.5 84 62.5 97" strokeWidth="3.2" strokeOpacity="0.3" />
            <path d="M39.5 64 Q37.5 72 38 79 Q36 88 32.5 97 M42 70 Q40.5 82 37.5 97 M60.5 64 Q62.5 72 62 79 Q64 88 67.5 97 M58 72 Q59.5 84 62.5 97" strokeWidth="1.4" />
          </g>
        </g>
      );
    case "angel":
      return (
        <g>
          {body}
          <path d="M31 96 L69 96 M37 72 L63 72" stroke={robe.accent} strokeWidth="2.2" strokeLinecap="round" />
          <Eye x={50} y={66} gold={robe.accent} />
          <Eye x={43} y={80} gold={robe.accent} />
          <Eye x={57} y={80} gold={robe.accent} />
          <Eye x={50} y={90} gold={robe.accent} />
        </g>
      );
    case "fish":
      return (
        <g>
          {body}
          {stars}
          {stripe}
          {/* a bass head bursting out of the chest */}
          <path d="M48 66 Q57 62 63 68 Q58 74 48 73 Z" fill="#7f9a3c" stroke={INK} strokeWidth="0.8" />
          <path d="M63 68 L58.5 66.5 L58.5 70.5 Z" fill="#3a0f12" />
          <circle cx="55" cy="67.5" r="1.1" fill="#d9a21b" />
          <circle cx="55" cy="67.5" r="0.5" fill={INK} />
        </g>
      );
    default:
      return (
        <g>
          {body}
          {stripe}
        </g>
      );
  }
}

function Hat({ hat }: { hat: Look }): ReactElement {
  switch (hat.shape) {
    case "topHat":
    case "tall":
      return (
        <g>
          <rect x="38" y="6" width="24" height="24" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <rect x="30" y="28" width="40" height="5" rx="2" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <rect x="38" y="22" width="24" height="3" fill={hat.accent} />
        </g>
      );
    case "catEars":
      return (
        <g>
          <path d="M33 34 Q50 26 67 34" fill="none" stroke="#222" strokeWidth="1.5" />
          <path d="M35 32 L39 13 L48 29 Z" fill={hat.color} stroke="#333" strokeWidth="1" />
          <path d="M38 30 L40 19 L45 29 Z" fill={hat.accent} />
          <path d="M65 32 L61 13 L52 29 Z" fill={hat.color} stroke="#333" strokeWidth="1" />
          <path d="M62 30 L60 19 L55 29 Z" fill={hat.accent} />
          <path d="M41 31 l-3.5 -2 v4 Z M41 31 l3.5 -2 v4 Z M59 31 l-3.5 -2 v4 Z M59 31 l3.5 -2 v4 Z" fill="#222" />
          <circle cx="41" cy="33.6" r="1.6" fill={GOLD} />
          <circle cx="59" cy="33.6" r="1.6" fill={GOLD} />
        </g>
      );
    case "viking":
      return (
        <g>
          <path d="M35 26 Q24 26 25 9 Q29 20 38 21 Z" fill={hat.accent} stroke={INK} strokeWidth="1" />
          <path d="M65 26 Q76 26 75 9 Q71 20 62 21 Z" fill={hat.accent} stroke={INK} strokeWidth="1" />
          <path d="M33 32 A17 15 0 0 1 67 32 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <path d="M50 17 L50 32" stroke={INK} strokeWidth="1" />
          <rect x="32" y="30" width="36" height="3" fill={hat.color} stroke={INK} strokeWidth="1" />
        </g>
      );
    case "crown":
      return (
        <g>
          <path d="M35 32 L35 18 L41 25 L50 14 L59 25 L65 18 L65 32 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
          <circle cx="50" cy="27" r="2.3" fill={hat.accent} />
          <circle cx="41" cy="28.5" r="1.4" fill="#2a6bd1" />
          <circle cx="59" cy="28.5" r="1.4" fill="#1f9e5a" />
          <circle cx="35" cy="18" r="1.3" fill="#f4efe4" />
          <circle cx="50" cy="14" r="1.3" fill="#f4efe4" />
          <circle cx="65" cy="18" r="1.3" fill="#f4efe4" />
        </g>
      );
    case "drinkingCap":
      return (
        <g>
          <path d="M28.5 29 L29 58 Q30 63 45 62 M71.5 29 L71 58 Q70 63 55 62" fill="none" stroke={hat.accent} strokeWidth="1.3" />
          <path d="M35 32 A15 13 0 0 1 65 32 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <rect x="32" y="30.5" width="36" height="2.5" fill={hat.color} stroke={INK} strokeWidth="1" />
          <rect x="25" y="17" width="7" height="12" rx="1" fill="#d8dde2" stroke={INK} strokeWidth="0.8" />
          <rect x="25" y="21" width="7" height="4" fill={hat.color} />
          <rect x="68" y="17" width="7" height="12" rx="1" fill="#d8dde2" stroke={INK} strokeWidth="0.8" />
          <rect x="68" y="21" width="7" height="4" fill={hat.color} />
        </g>
      );
    case "straw":
      return (
        <g>
          <path d="M38 31 A12 13 0 0 1 62 31 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <rect x="38" y="26.5" width="24" height="4" fill={hat.accent} />
          <ellipse cx="50" cy="31" rx="27" ry="4.5" fill={hat.color} stroke={INK} strokeWidth="1.5" />
        </g>
      );
    case "featheredCap":
      return (
        <g>
          <path d="M36 32 A14 10 0 0 1 64 32 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <path d="M60 31 Q68 30 71 33 L60 33 Z" fill={hat.color} stroke={INK} strokeWidth="1" />
          {/* the pigeon */}
          <path d="M43 18 L33 11 L34 18 L33 25 Z" fill="#f6f6f4" stroke={INK} strokeWidth="0.8" />
          <ellipse cx="50" cy="19" rx="9" ry="5.5" fill="#f6f6f4" stroke={INK} strokeWidth="1" />
          <circle cx="59" cy="14" r="3.6" fill="#f6f6f4" stroke={INK} strokeWidth="1" />
          <path d="M62.3 13.4 l3.2 1 l-3.2 1.1 Z" fill="#f0a37a" />
          <circle cx="60" cy="13.2" r="0.7" fill={INK} />
          <path d="M48 24 v3 M52 24 v3" stroke="#e0566e" strokeWidth="1.2" />
        </g>
      );
    case "volcano":
      return (
        <g>
          <circle cx="50" cy="9" r="3" fill="#9a948e" opacity="0.7" />
          <circle cx="53" cy="5" r="2.5" fill="#9a948e" opacity="0.6" />
          <circle cx="48" cy="2" r="2" fill="#9a948e" opacity="0.5" />
          <path d="M34 32 L44 14 L56 14 L66 32 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
          <path d="M44 14 Q50 12 56 14 Q50 16 44 14 Z" fill={hat.accent} />
          <path d="M47 15 L45.5 21 M54 15 L55.5 20" stroke={hat.accent} strokeWidth="1.6" strokeLinecap="round" />
        </g>
      );
    case "dunce":
      return (
        <g>
          <path d="M50 0 L64 32 L36 32 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" strokeLinejoin="round" />
          <text x="50" y="27.5" fontSize="4.6" fontWeight="bold" textAnchor="middle" fill={hat.accent} fontFamily="Arial, sans-serif">
            DUNCE
          </text>
        </g>
      );
    case "halo":
      return (
        <g fill="none">
          <ellipse cx="50" cy="24" rx="12" ry="3.5" stroke={hat.accent} strokeWidth="4" opacity="0.45" />
          <ellipse cx="50" cy="24" rx="12" ry="3.5" stroke={hat.color} strokeWidth="1.8" />
        </g>
      );
    case "miku":
      return (
        <g>
          {/* teal twin tails behind the head, bangs, pink-glowing headphones */}
          <path d="M33 34 Q22 50 26 82 Q30 70 34 50 Z" fill={hat.color} stroke={INK} strokeWidth="1" />
          <path d="M67 34 Q78 50 74 82 Q70 70 66 50 Z" fill={hat.color} stroke={INK} strokeWidth="1" />
          <circle cx="33" cy="34" r="2.6" fill="#555a60" stroke={hat.accent} strokeWidth="0.8" />
          <circle cx="67" cy="34" r="2.6" fill="#555a60" stroke={hat.accent} strokeWidth="0.8" />
          <path d="M33 33 Q34 20 50 20 Q66 20 67 33 L62 31 L58 34 L54 31 L50 34 L46 31 L42 34 L38 31 Z" fill={hat.color} stroke={INK} strokeWidth="1.2" strokeLinejoin="round" />
          <path d="M33 30 Q33 18 50 17 Q67 18 67 30" fill="none" stroke="#2a2a30" strokeWidth="1.6" />
          <rect x="29" y="38" width="5" height="9" rx="2" fill="#2a2a30" stroke={hat.accent} strokeWidth="1" />
          <rect x="66" y="38" width="5" height="9" rx="2" fill="#2a2a30" stroke={hat.accent} strokeWidth="1" />
          <text x="72" y="16" fontSize="7" fill={hat.accent} fontFamily="sans-serif">♫</text>
        </g>
      );
    case "fishCap":
      return (
        <g>
          <path d="M35 32 A15 12 0 0 1 65 32 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <path d="M60 31 Q69 30 72 33 L60 33 Z" fill={hat.color} stroke={INK} strokeWidth="1" />
          {/* the bass: tail out the back, head out the front, dorsal fin on top */}
          <path d="M35 22 L27 16 L28.5 22 L27 28 Z" fill="#5f7a2c" stroke={INK} strokeWidth="0.8" />
          <path d="M42 18 l2 -4.5 l2 3.5 l2 -4.5 l2 3.5 l2 -4 l2 3.5" fill="#5f7a2c" stroke={INK} strokeWidth="0.6" />
          <path d="M34 22 Q48 13 62 18.5 Q67 21 66 23.5 Q62 27.5 48 27 Q40 26 34 22 Z" fill="#7f9a3c" stroke={INK} strokeWidth="1" />
          <path d="M66.5 22.5 L61.5 21.5 L62 25 Z" fill="#3a0f12" />
          <circle cx="59.5" cy="21" r="1.1" fill="#d9a21b" />
          <circle cx="59.5" cy="21" r="0.5" fill={INK} />
        </g>
      );
    default:
      return (
        <g>
          <path d="M50 2 L66 30 L34 30 Z" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <ellipse cx="50" cy="31" rx={hat.shape === "wide" ? 28 : 20} ry="4" fill={hat.color} stroke={INK} strokeWidth="1.5" />
          <path d="M47 14 l3-3 3 3 -3 3z" fill={hat.accent} />
        </g>
      );
  }
}

export function WizardPortrait({ wizard, size = 160, title }: { wizard: WizardLook; size?: number; title?: string }) {
  const hat = look(wizard.hat, { color: "#3a2a8c", accent: "#ffd700", shape: "cone" });
  const robe = look(wizard.robe, { color: "#3a2a8c", accent: "#ffd700" });
  const head = look(wizard.head, { color: "#e8dfc8", accent: "#33ff66", shape: "crt" });
  return (
    <svg
      className="wizard-portrait"
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <Robe robe={robe} />
      {/* hands holding the screen like the sketch */}
      <circle cx="36" cy="70" r="4" fill="#e8c4a0" stroke={INK} strokeWidth="1" />
      <circle cx="64" cy="70" r="4" fill="#e8c4a0" stroke={INK} strokeWidth="1" />
      {/* computer head */}
      {head.shape === "flat" ? (
        <rect x="31" y="34" width="38" height="26" rx="2" fill={head.color} stroke={INK} strokeWidth="1.5" />
      ) : (
        <rect x="32" y="32" width="36" height="30" rx={head.shape === "round" ? 10 : 4} fill={head.color} stroke={INK} strokeWidth="1.5" />
      )}
      <rect x="37" y="37" width="26" height="19" rx="2" fill="#081008" />
      <g fill={head.accent}>
        <rect x="42" y="42" width="3" height="3" />
        <rect x="55" y="42" width="3" height="3" />
        <rect x="43" y="50" width="14" height="2" />
      </g>
      <Hat hat={hat} />
    </svg>
  );
}
